import React, { useState, useEffect } from 'react';
import {
    Box, TableCell, TableRow, IconButton, Button, Collapse, Tooltip, Typography, Table, TableHead, TableBody
} from '@mui/material';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import supabase from '../../../supabaseClient';
import AddPaymentModal from './AddPaymentModal';
import PaymentHistory from './PaymentHistory';
import { applyRoundingRules, findMatchingRule } from '../../../Utils/roundingRules';

function EmployeeRow({ employee, dateFrom, dateTo, idConfig, roundingConfig, rules = [], getShiftTooltip, onPaymentAdded, onLifetimeChange }) {
  const roundTimestamp = (dateStr, cfg) => {
    const d = new Date(dateStr);
    if (!cfg.enabled) return d;
    const rem = d.getMinutes() + d.getSeconds() / 60;
    if (cfg.direction === 'up'   && 60 - rem <= cfg.thresholdMinutes) d.setHours(d.getHours() + 1, 0, 0, 0);
    if (cfg.direction === 'down' && rem       <= cfg.thresholdMinutes) d.setMinutes(0, 0, 0);
    return d;
  };

  const [open, setOpen] = useState(false);
  const [payments, setPayments] = useState([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [lifetimeRemaining, setLifetimeRemaining] = useState(null);

  const fetchPayments = async () => {
    const { data, error } = await supabase
      .from('payments')
      .select('*')
      .eq('employee_id', employee.id)
      .gte('date', dateFrom)
      .lte('date', dateTo)
      .order('date', { ascending: false });
    if (!error) setPayments(data || []);
  };

  const fetchLifetimeBalance = async () => {
    const [{ data: attendanceData }, { data: paymentsData }] = await Promise.all([
      supabase
        .from('attendance')
        .select('check_in, check_out')
        .eq('employee_id', employee.id)
        .not('check_in', 'is', null)
        .not('check_out', 'is', null),
      supabase
        .from('payments')
        .select('amount')
        .eq('employee_id', employee.id),
    ]);

    const totalMins = (attendanceData || []).reduce((acc, r) => {
      const matched = findMatchingRule(r.check_in, rules);
      if (matched) {
        return acc + applyRoundingRules(r.check_in, r.check_out, matched).billableMinutes;
      }
      const ci = roundTimestamp(r.check_in,  roundingConfig.checkIn);
      const co = roundTimestamp(r.check_out, roundingConfig.checkOut);
      return acc + Math.max(0, (co - ci) / 60000);
    }, 0);

    const totalEarned = (totalMins / 60) * (employee.hourly_rate || 0);
    const totalPaidEver = (paymentsData || []).reduce((acc, p) => acc + p.amount, 0);
    const remaining = Math.max(0, totalEarned - totalPaidEver);
    setLifetimeRemaining(remaining);
    onLifetimeChange?.(employee.id, remaining);
  };

  useEffect(() => {
    fetchPayments();
  }, [dateFrom, dateTo]);

  useEffect(() => {
    fetchLifetimeBalance();
  }, [rules]);

  const handlePaymentAdded = () => {
    fetchPayments();
    fetchLifetimeBalance();
    onPaymentAdded();
  };

  const calcDuration = (r) => {
    const matched = findMatchingRule(r.check_in, rules);
    if (matched) return applyRoundingRules(r.check_in, r.check_out, matched).billableMinutes;
    const ci = roundTimestamp(r.check_in,  roundingConfig.checkIn);
    const co = roundTimestamp(r.check_out, roundingConfig.checkOut);
    return Math.max(0, (co - ci) / 60000);
  };

  const fmtDate = (iso) => new Date(iso).toLocaleDateString('pl-PL');
    const fmtTime = (iso) => iso
    ? new Date(iso).toLocaleTimeString('pl-PL', { hour: '2-digit', minute: '2-digit' })
    : '—';
    const fmtMins = (m) => `${Math.floor(m / 60)}h ${Math.round(m % 60)}m`;

  const totalMinutes = employee.records.reduce((acc, r) => {
    if (!r.check_in || !r.check_out) return acc;
    return acc + calcDuration(r);
  }, 0);

  // Dominant rule name for this employee's records
  const dominantRule = (() => {
    if (!rules.length) return null;
    const counts = {};
    employee.records.forEach(r => {
      const m = findMatchingRule(r.check_in, rules);
      if (m) counts[m.name] = (counts[m.name] || 0) + 1;
    });
    const entries = Object.entries(counts);
    if (!entries.length) return null;
    return entries.sort((a, b) => b[1] - a[1])[0][0];
  })();

  const totalHours = totalMinutes / 60;
  const displayHours = `${Math.floor(totalHours)}h ${Math.round(totalMinutes % 60)}m`;
  const salary = totalHours * (employee.hourly_rate || 0);
  const totalPaid = payments.reduce((acc, p) => acc + p.amount, 0);
  const remaining = salary - totalPaid;

  return (
    <>
      <TableRow hover sx={{ cursor: 'pointer' }} onClick={() => setOpen(o => !o)}>
        <TableCell>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <IconButton size="small" onClick={e => { e.stopPropagation(); setOpen(o => !o); }}>
              {open ? <KeyboardArrowDownIcon /> : <KeyboardArrowRightIcon />}
            </IconButton>
            {employee.name} {employee.surname}
          </Box>
        </TableCell>
        <TableCell>{displayHours}</TableCell>
        <TableCell sx={{ color: 'text.secondary' }}>—</TableCell>
        <TableCell sx={{ color: 'text.secondary' }}>—</TableCell>
        <TableCell sx={{ color: 'text.secondary' }}>{employee.hourly_rate?.toFixed(2)} zł</TableCell>
        <TableCell>
          <Tooltip title={getShiftTooltip ? employee.records.map(r => getShiftTooltip(r)).join('\n') : ''} arrow>
            <span>{salary.toFixed(2)} zł</span>
          </Tooltip>
        </TableCell>
        <TableCell sx={{ color: 'text.secondary' }}>{totalPaid.toFixed(2)} zł</TableCell>
        <TableCell sx={{ color: remaining > 0 ? 'success.main' : 'text.secondary' }}>
          {remaining.toFixed(2)} zł
        </TableCell>
        <TableCell sx={{ color: lifetimeRemaining > 0 ? 'error.main' : 'text.secondary', fontWeight: 500 }}>
          {lifetimeRemaining !== null ? `${lifetimeRemaining.toFixed(2)} zł` : '—'}
        </TableCell>
        <TableCell sx={{ color: 'text.secondary', fontSize: '0.78rem' }}>
          {dominantRule ?? '—'}
        </TableCell>
        <TableCell>
          <Button size="small" variant="outlined"
            onClick={e => { e.stopPropagation(); setModalOpen(true); }}>
            + Wypłata
          </Button>
        </TableCell>
      </TableRow>

      <TableRow>
        <TableCell colSpan={10} sx={{ p: 0, border: 0 }}>
          <Collapse in={open} timeout="auto" unmountOnExit>
            <Box sx={{ backgroundColor: 'rgba(0,0,0,0.02)', px: 6, py: 1.5 }}>
              <Box sx={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'flex-start' }}>
                {/* LEWA: Historia obecności */}
                <Box sx={{ flex: 1, minWidth: 380 }}>
                  <Box sx={{ color: 'text.secondary', fontWeight: 600, fontSize: '0.75rem', mb: 1 }}>
                    Historia obecności
                  </Box>
                  {employee.records.length === 0 ? (
                    <Typography variant="body2" sx={{ color: 'text.secondary', pb: 1 }}>
                      Brak obecności w tym okresie
                    </Typography>
                  ) : (
                    <Table size="small" sx={{ width: 'auto', minWidth: 360 }}>
                      <TableHead>
                        <TableRow>
                          <TableCell sx={{ color: 'text.secondary', fontSize: '0.75rem', py: 0.5, pl: 0 }}>Data</TableCell>
                          <TableCell sx={{ color: 'text.secondary', fontSize: '0.75rem', py: 0.5 }}>Wejście</TableCell>
                          <TableCell sx={{ color: 'text.secondary', fontSize: '0.75rem', py: 0.5 }}>Wyjście</TableCell>
                          <TableCell sx={{ color: 'text.secondary', fontSize: '0.75rem', py: 0.5 }} align="right">Czas</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {[...employee.records]
                          .sort((a, b) => new Date(b.check_in) - new Date(a.check_in))
                          .map((r, i) => (
                          <TableRow key={i}>
                            <TableCell sx={{ py: 0.5, pl: 0, fontSize: '0.85rem', color: 'text.secondary', border: 0 }}>{fmtDate(r.check_in)}</TableCell>
                            <TableCell sx={{ py: 0.5, fontSize: '0.85rem', color: 'text.secondary', border: 0 }}>{fmtTime(r.check_in)}</TableCell>
                            <TableCell sx={{ py: 0.5, fontSize: '0.85rem', color: r.check_out ? 'text.secondary' : 'warning.main', fontStyle: r.check_out ? 'normal' : 'italic', border: 0 }}>
                              {r.check_out ? fmtTime(r.check_out) : 'pracuje'}
                            </TableCell>
                            <TableCell sx={{ py: 0.5, fontSize: '0.85rem', color: 'text.secondary', border: 0 }} align="right">
                              {r.check_out ? fmtMins(calcDuration(r)) : '—'}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  )}
                </Box>

                {/* PRAWA: Historia wypłat (bez zmian) */}
                <Box sx={{ flex: 1, minWidth: 380 }}>
                  <Box sx={{ color: 'text.secondary', fontWeight: 600, fontSize: '0.75rem', mb: 1 }}>
                    Historia wypłat
                  </Box>
                  <PaymentHistory
                    payments={payments}
                    onDeleted={handlePaymentAdded}
                    onEdited={handlePaymentAdded}
                  />
                </Box>

              </Box>
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>

      <AddPaymentModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        employee={employee}
        idConfig={idConfig}
        onRefresh={handlePaymentAdded}
      />
    </>
  );
}

export default EmployeeRow;
